import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';
import { createExtra } from '@helpers/api/mantenedores/extras';

const NuevoExtraModal = ({ show, onHide, onExtraCreated, user, tiposExtra }) => {
  const [formData, setFormData] = useState({
    extra: '',
    monto: '',
    tipo_extra_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Resetear formulario cuando se abre/cierra el modal
  React.useEffect(() => {
    if (show) {
      setFormData({
        extra: '',
        monto: '',
        tipo_extra_id: ''
      });
      setError(null);
      setSuccess(null);
    }
  }, [show]);

  // Manejar cambios en el formulario
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Limpiar errores al cambiar valores
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  // Validación del formulario
  const validateForm = () => {
    if (!formData.extra || formData.extra.trim() === '') {
      setError('El nombre del extra es requerido');
      return false;
    }

    if (!formData.monto || Number(formData.monto) <= 0) {
      setError('El monto debe ser mayor a 0');
      return false;
    }

    if (!formData.tipo_extra_id) {
      setError('El tipo de extra es requerido');
      return false;
    }

    // Validar que el monto sea un número entero
    if (!Number.isInteger(Number(formData.monto))) {
      setError('El monto debe ser un número entero sin decimales');
      return false;
    }
    
    return true;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const dataToSend = {
        extra: formData.extra.trim(),
        monto: Number(formData.monto),
        tipo_extra_id: Number(formData.tipo_extra_id)
      };

      const response = await createExtra(user, dataToSend);

      if (response.success) {
        setSuccess('Extra creado exitosamente');
        setTimeout(() => {
          onExtraCreated();
        }, 1500);
      } else {
        setError(response.error || 'Error al crear el extra');
      }
    } catch (err) {
      setError('Error al crear el extra');
    } finally {
      setLoading(false);
    }
  };

  // Formatear monto mientras se escribe
  const formatMontoInput = (value) => {
    // Eliminar caracteres no numéricos
    const cleanValue = value.replace(/[^\d]/g, '');
    return cleanValue;
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title style={{ 
          color: '#212529', 
          fontSize: '1.25rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <FaIcons.FaPlus style={{ color: '#28a745' }} />
          Nuevo Extra
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ padding: '24px' }}>
          {/* Mensajes de estado */}
          {error && (
            <Alert variant="danger" style={{ marginBottom: '20px' }}>
              <FaIcons.FaExclamationTriangle style={{ marginRight: '8px' }} />
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" style={{ marginBottom: '20px' }}>
              <FaIcons.FaCheckCircle style={{ marginRight: '8px' }} />
              {success}
            </Alert>
          )}

        {/* Tipo de Extra */}
        <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: '500', color: '#495057' }}>
                Tipo de Extra <span style={{ color: '#dc3545' }}>*</span>
            </Form.Label>
            <Form.Select
                value={formData.tipo_extra_id}
                onChange={(e) => handleChange('tipo_extra_id', e.target.value)}
                required
                disabled={loading}
                style={{ fontSize: '14px' }}
            >
                <option value="">Selecciona un tipo de extra</option>
                {tiposExtra.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                        {tipo.tipo.toUpperCase()}
                    </option>
                ))}
            </Form.Select>
        </Form.Group>

        {/* Nombre del Extra */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: '500', color: '#495057' }}>
              Nombre del Extra <span style={{ color: '#dc3545' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.extra}
              onChange={(e) => handleChange('extra', e.target.value)}
              placeholder="Ingresa el nombre del extra"
              required
              disabled={loading}
              maxLength={100}
              style={{ fontSize: '14px' }}
            />
            <Form.Text className="text-muted">
              Máximo 100 caracteres
            </Form.Text>
          </Form.Group>

          {/* Monto */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: '500', color: '#495057' }}>
              Monto Sugerido <span style={{ color: '#dc3545' }}>*</span>
            </Form.Label>
            <InputGroup>
              <InputGroup.Text style={{ backgroundColor: '#f8f9fa' }}>
                <FaIcons.FaDollarSign />
              </InputGroup.Text>
              <Form.Control
                type="text"
                value={formData.monto}
                onChange={(e) => handleChange('monto', formatMontoInput(e.target.value))}
                placeholder="0"
                required
                disabled={loading}
                style={{ fontSize: '14px', fontFamily: 'monospace' }}
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Solo números enteros, sin decimales. Debe ser mayor a 0.
            </Form.Text>
            {formData.monto && (
              <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#28a745',
                fontWeight: '500'
              }}>
                Formato: {new Intl.NumberFormat('es-CL', {
                  style: 'currency',
                  currency: 'CLP',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(Number(formData.monto) || 0)}
              </div>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer style={{ 
          borderTop: '1px solid #dee2e6',
          padding: '16px 24px'
        }}>
          <Button 
            variant="secondary" 
            onClick={onHide}
            disabled={loading}
            style={{ 
              fontSize: '14px',
              padding: '8px 20px'
            }}
          >
            Cancelar
          </Button>
          <Button 
            variant="success" 
            type="submit"
            disabled={loading || success}
            style={{ 
              fontSize: '14px',
              padding: '8px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <div 
                  className="spinner-border spinner-border-sm" 
                  role="status"
                  style={{ width: '16px', height: '16px' }}
                >
                  <span className="visually-hidden">Cargando...</span>
                </div>
                Creando...
              </>
            ) : success ? (
              <>
                <FaIcons.FaCheck />
                Creado
              </>
            ) : (
              <>
                <FaIcons.FaPlus />
                Crear Extra
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

NuevoExtraModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onExtraCreated: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  tiposExtra: PropTypes.array.isRequired
};

export default NuevoExtraModal;
